/**
 * Manager Service - API calls specific to manager journey
 * Handles team management, objective approvals, evidence classification, etc.
 */

import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

export interface DirectReport {
  employee_id: string
  email: string
  display_name: string
  job_title: string | null
  department: string | null
  campus: string | null
  manager_id: string
  is_active: boolean
  roles: string[]
  // Worker-level employment classification (from F&O sync). ICA/Casual workers
  // don't take part in the performance cycle — see isReviewEligible().
  position_type?: string | null
  employment_category?: string | null
  review_eligible?: boolean
  // Stand-in people leader (absentee-approver hierarchy): this report's Line
  // manager seat is vacant and the viewer leads them as acting people leader.
  acting_people_leader?: boolean
  line_manager_vacant?: boolean
  vacant_line_seat?: string | null
}

export interface FOEmployeeData {
  personnel_number: string
  party_number: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  known_as: string
  worker_status: string
  worker_type: string
  title_id: string
  office_location: string
  campus: string
  department: string
  job_title: string
  original_hire_date: string
  position_id: string
  position_description: string
  job_id: string
  job_family_id: string
  functional_area: string
  reports_to_position_id: string
  manager_personnel_number: string
  is_primary_position: string
}

export interface TeamMember extends DirectReport {
  foData?: FOEmployeeData
  objectivesCount?: number
  kpisCount?: number
  evidenceCount?: number
  upcomingReviews?: number
  lastReviewDate?: string
  overallProgress?: number
  status?: 'on-track' | 'at-risk' | 'off-track' | 'pending'
  
  // Enriched data from backend
  objectives?: {
    total: number
    onTrack: number
    atRisk: number
    offTrack: number
    completed: number
    pending_approval: number
    progress: number
  }

  // Full objective list for this member's plan (titles + workflow status).
  // Already populated by getTeamMembersWithDetails; surfaced on the type so
  // the team-card can list what the member is actually working on.
  planObjectives?: Array<{
    title: string
    status?: string
    [key: string]: any
  }>

  nextReview?: {
    type: string
    date: string | null
    status: 'scheduled' | 'pending-scheduling' | 'overdue' | 'completed'
    daysUntil: number | null
  }
  
  performance?: {
    current: 'exceptional' | 'full' | 'not_yet_full' | 'weak' | null
    trend: 'improving' | 'maintaining' | 'declining' | null
    lastRating: string | null
    lastReviewDate: string | null
  }
  
  alerts?: string[]
}

export interface TeamPerformanceSummary {
  managerId: string
  teamSize: number
  totalObjectives: number
  totalKPIs: number
  averageProgress: number
  pendingApprovals: number
  upcomingReviews: number
  atRiskCount: number
  onTrackCount: number
  offTrackCount: number
}

class ManagerService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      },
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    if (response.status === 204) {
      return null as T
    }

    return response.json()
  }
  /**
   * Get direct reports for a manager from Identity Service
   */
  async getDirectReports(managerId: string): Promise<DirectReport[]> {
    return this.request<DirectReport[]>(`/identity/users/${managerId}/direct-reports`)
  }

  /**
   * Get F&O employee data for enriched team member information
   */
  async getFOEmployeeData(personnelNumber: string): Promise<FOEmployeeData> {
    return this.request<FOEmployeeData>(`/integration/f0/employees/${personnelNumber}`)
  }

  /**
   * Get enriched team members with both Identity and F&O data
   */
  async getEnrichedTeamMembers(managerId: string): Promise<TeamMember[]> {
    // Get direct reports from identity service
    const directReports = await this.getDirectReports(managerId)
    
    // Enrich each with F&O data
    const enrichedMembers = await Promise.all(
      directReports.map(async (report) => {
        try {
          const foData = await this.getFOEmployeeData(report.employee_id)
          return {
            ...report,
            foData,
            // Override with more reliable F&O data
            display_name: foData.full_name || report.display_name,
            job_title: foData.job_title || report.job_title,
            department: foData.functional_area || foData.department || report.department,
            campus: foData.campus || report.campus
          }
        } catch (error) {
          console.warn(`Failed to fetch F&O data for ${report.employee_id}:`, error)
          return report
        }
      })
    )
    
    return enrichedMembers
  }

  /**
   * Get fully enriched team summary with all performance data from backend
   * This is the NEW enriched endpoint that includes objectives, reviews, performance, alerts
   */
  async getEnrichedTeamSummary(managerId: string): Promise<{
    manager_id: string
    team_members: any[]
    summary: any
    /** True only if this manager manages someone who themselves has reports.
     *  Drives calibration/talent scheduling gates (the 'manager' role alone
     *  is unreliable — auto-derived and over-assigned). */
    is_one_up?: boolean
  }> {
    return this.request(`/performance/manager/${managerId}/team-summary`)
  }

  /**
   * Get performance metrics for team members
   * Aggregates objectives, KPIs, evidence for each member
   */
  async getTeamPerformanceMetrics(
    teamMembers: TeamMember[],
    year: number = new Date().getFullYear()
  ): Promise<TeamMember[]> {
    const membersWithMetrics = await Promise.all(
      teamMembers.map(async (member) => {
        try {
          // Dynamic import to avoid circular dependency
          const { performanceService } = await import('./performanceService')
          
          // Get performance plan
          const plan = await performanceService.getEmployeePerformancePlan(
            member.employee_id,
            year,
            false
          )
          
          // Get evidence count
          let evidenceCount = 0
          try {
            const evidence = await performanceService.getUserEvidence(member.employee_id)
            evidenceCount = evidence.evidence?.length || 0
          } catch (error) {
            console.warn(`Failed to fetch evidence for ${member.employee_id}`)
          }
          
          // Get review milestones and performance rating from completed reviews
          let upcomingReviews = 0
          let lastReviewDate: string | undefined
          let performanceRating: 'exceptional' | 'full' | 'not_yet_full' | 'weak' | null = null
          let lastRatingType: string | null = null
          
          try {
            const reviews = await performanceService.getUserReviewMilestones(
              member.employee_id,
              year,
              true
            )
            upcomingReviews = reviews.milestones?.filter((m: any) => 
              m.status === 'scheduled' && new Date(m.due_date) > new Date()
            ).length || 0
            
            const completedReviews = reviews.milestones?.filter((m: any) => m.status === 'completed')
            if (completedReviews && completedReviews.length > 0) {
              // Use updated_at as the completion timestamp for completed reviews
              const latestReview = completedReviews.sort((a: any, b: any) => 
                new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime()
              )[0]
              lastReviewDate = latestReview.updated_at ?? undefined
              lastRatingType = latestReview.review_type ?? null
            }
          } catch (error) {
            console.warn(`Failed to fetch reviews for ${member.employee_id}`)
          }
          
          // Fetch actual review sessions to get ratings
          try {
            const { reviewService } = await import('./reviewService')
            const reviewSessions = await reviewService.getUserReviewCycle(member.employee_id, year)
            
            // Find most recent completed review with a rating
            const completedWithRating = reviewSessions
              .filter((r: any) => r.status === 'completed' && (r.calibrated_rating || r.initial_rating))
              .sort((a: any, b: any) => 
                new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime()
              )
            
            if (completedWithRating.length > 0) {
              const rating = completedWithRating[0].calibrated_rating || completedWithRating[0].initial_rating
              // Normalize rating format (handle both 'not-yet-full' and 'not_yet_full')
              if (rating) {
                performanceRating = rating.replace(/-/g, '_') as 'exceptional' | 'full' | 'not_yet_full' | 'weak'
              }
              lastRatingType = completedWithRating[0].review_type ?? null
              lastReviewDate = completedWithRating[0].updated_at || completedWithRating[0].completed_date || undefined
            }
          } catch (error) {
            console.warn(`Failed to fetch review sessions for ${member.employee_id}`)
          }
          
          // Calculate objectives breakdown from plan data
          const objectivesBreakdown = {
            total: 0,
            onTrack: 0,
            atRisk: 0,
            offTrack: 0,
            completed: 0,
            pending_approval: 0,
            rejected: 0,
            progress: plan.overall_progress || 0
          }
          
          // Determine status based on KPI statuses
          let status: 'on-track' | 'at-risk' | 'off-track' | 'pending' = 'pending'
          if (plan.has_objectives && plan.objectives.length > 0) {
            // Calculate objective-level status from KPIs — only active objectives count for performance
            plan.objectives.forEach((obj: any) => {
              const objStatus = obj.status?.toLowerCase() || ''
              
              // Skip non-active workflow statuses
              if (objStatus === 'pending_approval' || objStatus === 'pending' || objStatus === 'draft') {
                objectivesBreakdown.pending_approval++
                return
              }
              if (objStatus === 'rejected') {
                objectivesBreakdown.rejected++
                return
              }
              if (objStatus === 'withdrawn' || objStatus === 'completed') {
                if (objStatus === 'completed') objectivesBreakdown.completed++
                return
              }
              
              // Only active objectives count toward total and performance tracking
              objectivesBreakdown.total++
              
              // For active objectives, calculate performance status from KPIs
              const objKpis = obj.kpis || []
              if (objKpis.length === 0) {
                objectivesBreakdown.onTrack++
                return
              }
              
              // Determine objective status based on worst KPI status
              const hasOffTrack = objKpis.some((kpi: any) => kpi.status === 'off-track')
              const hasAtRisk = objKpis.some((kpi: any) => kpi.status === 'at-risk')
              
              if (hasOffTrack) {
                objectivesBreakdown.offTrack++
              } else if (hasAtRisk) {
                objectivesBreakdown.atRisk++
              } else {
                objectivesBreakdown.onTrack++
              }
            })
            
            // Determine overall member status based on objectives
            if (objectivesBreakdown.offTrack > 0) {
              status = 'off-track'
            } else if (objectivesBreakdown.atRisk > 0) {
              status = 'at-risk'
            } else if (objectivesBreakdown.onTrack > 0) {
              status = 'on-track'
            }
          }
          
          return {
            ...member,
            objectivesCount: objectivesBreakdown.total,
            kpisCount: plan.total_kpis,
            evidenceCount,
            upcomingReviews,
            lastReviewDate,
            overallProgress: plan.overall_progress,
            status,
            objectives: objectivesBreakdown,
            planObjectives: plan.objectives || [],
            performance: performanceRating ? {
              current: performanceRating,
              trend: null,
              lastRating: lastRatingType,
              lastReviewDate: lastReviewDate || null
            } : undefined
          }
        } catch (error) {
          console.error(`Failed to fetch metrics for ${member.employee_id}:`, error)
          return {
            ...member,
            status: 'pending' as const
          }
        }
      })
    )
    
    return membersWithMetrics
  }

  /**
   * Get aggregated team performance summary
   */
  async getTeamPerformanceSummary(
    managerId: string,
    year: number = new Date().getFullYear()
  ): Promise<TeamPerformanceSummary> {
    const enrichedTeam = await this.getEnrichedTeamMembers(managerId)
    const teamWithMetrics = await this.getTeamPerformanceMetrics(enrichedTeam, year)
    
    return {
      managerId,
      teamSize: teamWithMetrics.length,
      totalObjectives: teamWithMetrics.reduce((sum, m) => sum + (m.objectivesCount || 0), 0),
      totalKPIs: teamWithMetrics.reduce((sum, m) => sum + (m.kpisCount || 0), 0),
      averageProgress: teamWithMetrics.length > 0
        ? teamWithMetrics.reduce((sum, m) => sum + (m.overallProgress || 0), 0) / teamWithMetrics.length
        : 0,
      pendingApprovals: 0, // TODO: Need to fetch pending objectives
      upcomingReviews: teamWithMetrics.reduce((sum, m) => sum + (m.upcomingReviews || 0), 0),
      atRiskCount: teamWithMetrics.filter(m => m.status === 'at-risk').length,
      onTrackCount: teamWithMetrics.filter(m => m.status === 'on-track').length,
      offTrackCount: teamWithMetrics.filter(m => m.status === 'off-track').length
    }
  }

  /**
   * Approve an objective (manager action)
   */
  async approveObjective(objectiveId: number, approverId: string): Promise<void> {
    // TODO: Update to use proper approval endpoint when available
    // For now, update objective status
    return this.request(`/api/v1/objectives/${objectiveId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'approved',
        approved_by: approverId,
        approved_at: new Date().toISOString()
      })
    })
  }

  /**
   * Reject an objective with reason (manager action)
   */
  async rejectObjective(
    objectiveId: number,
    rejecterId: string,
    reason: string
  ): Promise<void> {
    return this.request(`/api/v1/objectives/${objectiveId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: 'rejected',
        rejected_by: rejecterId,
        rejected_at: new Date().toISOString(),
        rejection_reason: reason
      })
    })
  }

  /**
   * Classify team member's evidence (manager perspective)
   */
  async classifyTeamMemberEvidence(
    userId: string,
    evidenceId: string,
    classification: 'exceptional' | 'full' | 'not-yet-full' | 'weak'
  ): Promise<void> {
    // Dynamic import to avoid circular dependency
    const { performanceService } = await import('./performanceService')
    
    return performanceService.updateEvidenceClassification(userId, evidenceId, classification)
  }
}

// Export singleton instance
const managerService = new ManagerService()
export default managerService
